// filepath: src/app/core/store/effects/resource-fetch.util.ts
import { Actions, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { Store } from '@ngrx/store';
import { isEqual, isObject } from 'lodash';
import { NotificationService } from '../../services/notification.service';
import 'reflect-metadata';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ResourceActionTypes, ResourceCancel, ResourceFailed, ResourceStart, ResourceSuccess } from '@completion/actions';
import type { ResourceDescriptor } from '@completion/models';
import type { State } from '@completion/reducers';
import { BaseService, RESOURCE_IDENTIFIERS_KEY, RESOURCE_TYPE_KEY } from '@completion/services';
import { HttpErrorResponse } from '@angular/common/http';

// Small helper to keep types narrow where useful
export type Constructable<T extends Action = Action> = new (...args: any[]) => T;
const passThrough = (...args: any[]) => args;

/**
 * Build a ResourceDescriptor using the same metadata strategy as ResourceEffect.
 * Falls back gracefully if service isn't a BaseService or lacks metadata (e.g., mocks in tests).
 */
function buildDescriptor(action: Action, service: BaseService, resourcePropertyKey: string | symbol): ResourceDescriptor {
  const resourceTypeMetadata = Reflect.getMetadata(RESOURCE_TYPE_KEY, service) || {};
  const resourceIdentifiersMetadata = Reflect.getMetadata(RESOURCE_IDENTIFIERS_KEY, service) || [];

  // Service may not extend BaseService (e.g. mocks) or method may lack identifiers
  if (!resourceIdentifiersMetadata[resourcePropertyKey]) {
    return resourceTypeMetadata[resourcePropertyKey];
  }

  const identifiers: Record<string, unknown> = {};
  resourceIdentifiersMetadata[resourcePropertyKey].forEach((identifierName: string) => {
    const actAny = action as any; // action type is dynamic from effect stream; access by convention
    identifiers[identifierName] = actAny?.[identifierName];
  });

  return {
    type: resourceTypeMetadata[resourcePropertyKey],
    identifiers,
  } as ResourceDescriptor;
}

export interface TrackResourceOptions<R = unknown, A extends Action = Action> {
  actions$: Actions;
  store: Store<State>;
  toastr?: NotificationService; // Optional, errors are still tracked via ResourceFailed
  service: BaseService | Record<string, any>;
  method: string; // method name on service
  successAction?: Constructable<Action>; // optional follow-up action constructor
  /** Alternative success mapper that can emit one or many actions from the resource. If provided, it takes precedence over successAction/mapSuccessArgs. */
  onSuccess?: (resource: R, sourceArgs?: any[]) => Action | Action[] | null | undefined;
  /** Map upstream effect values to service method args. Receives the raw upstream emission(s). */
  serviceArgs?: (...sourceArgs: any[]) => any[];
  /** Map service response (and optionally source args) to success action constructor args. */
  mapSuccessArgs?: (resource: R, sourceArgs?: any[]) => any[];
  /** Optional override for descriptor; otherwise built from service metadata. */
  descriptor?: ResourceDescriptor | ((action: A) => ResourceDescriptor);
  /** Optional error action to keep existing reducer flows (e.g., UpdateError). */
  errorAction?: Constructable<Action>;
  /** Map error (and optionally source args) to error action constructor args. */
  mapErrorArgs?: (error: any, sourceArgs?: any[]) => any[];
  /** Optional error mapper for custom branching; if provided, takes precedence over errorAction/mapErrorArgs. */
  onError?: (error: any, sourceArgs?: any[]) => Action | Action[] | null | undefined;
}

/**
 * Drop-in operator to wrap an existing service call with ResourceStart/Success/Failed tracking and cancelation.
 */
export function trackResource<R = unknown, A extends Action = Action>(
  opts: TrackResourceOptions<R, A>,
): (input$: Observable<any>) => Observable<Action> {
  const {
    actions$,
    store,
    toastr,
    service,
    method,
    successAction,
    serviceArgs = passThrough,
    mapSuccessArgs = passThrough,
    descriptor,
    errorAction,
    mapErrorArgs = passThrough,
    onError,
  } = opts;

  return (input$: Observable<any>) =>
    input$.pipe(
      mergeMap((maybeAction, ...rest) => {
        // If preceded by withLatestFrom, first element is the triggering action, otherwise maybeAction is the action.
        const action: A = Array.isArray(maybeAction) ? maybeAction[0] : (maybeAction as A);
        const serviceFn = (service as any)[method].bind(service);

        const rd: ResourceDescriptor =
          typeof descriptor === 'function'
            ? (descriptor as (a: A) => ResourceDescriptor)(action)
            : (descriptor as ResourceDescriptor) || buildDescriptor(action, service as BaseService, method);

        // Start tracking
        store.dispatch(new ResourceStart(rd));

        // Call the service with mapped args
        return (serviceFn as (...args: any[]) => Observable<R>)(...serviceArgs(maybeAction, ...rest)).pipe(
          // Success tracking
          tap(() => store.dispatch(new ResourceSuccess(rd))),
          // Optionally emit success actions: prefer onSuccess, then successAction/mapSuccessArgs
          switchMap((resource: R) => {
            if (opts.onSuccess) {
              const out = opts.onSuccess(resource, [maybeAction, ...rest]);
              return Array.isArray(out) ? of(...out) : out ? of(out) : EMPTY;
            }
            return successAction ? of(new (successAction as any)(...(mapSuccessArgs(resource, [maybeAction, ...rest]) as any[]))) : EMPTY;
          }),
          // Error handling mirrors ResourceEffect semantics, with optional error action emission
          catchError((err) => {
            let message: string;
            const status = err instanceof HttpErrorResponse ? err.status : undefined;
            if (isObject((err as any)?.error) && (err as any).error.error) {
              message = `${(err as any).error.error}: ${(err as any).error.message}`;
            } else if ((err as any)?.error) {
              message = `${(err as any).error}: ${(err as any).message || 'No error details provided'}`;
            } else if ((err as any)?.message) {
              message = (err as any).message;
            } else {
              message = String(err);
            }
            toastr?.error(message);
            store.dispatch(new ResourceFailed(rd, message, status));

            if (onError) {
              const out = onError(err, [maybeAction, ...rest]);
              return Array.isArray(out) ? of(...out) : out ? of(out) : EMPTY;
            }
            if (errorAction) {
              return of(new (errorAction as any)(...(mapErrorArgs(err, [maybeAction, ...rest]) as any[])));
            }
            // Keep parity with existing ResourceEffect when no error action mapping is provided
            return EMPTY;
          }),
          // Allow cancelation of this specific resource request
          takeUntil(
            actions$.pipe(
              ofType<ResourceCancel>(ResourceActionTypes.Cancel),
              filter((cancel) => isEqual(cancel.resourceDescriptor, rd)),
            ),
          ),
        );
      }),
    );
}
